import React, { useEffect } from 'react';
import { type WrappedComponentProps, injectIntl } from 'react-intl-next';

import { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import CrossJoinHeroImage from './confluence-availability-hero.gif';
import HeroBackground from './foundational-blue-work-management.png';
import { messages } from './messages';
import { useAnalytics } from '../../../../common/analytics';

const styles = cssMap({
	heroImageContainer: {
		position: 'relative',
		borderRadius: token('border.radius.300'),
		overflow: 'hidden',
	},

	heroBackground: {
		height: '450px',
		width: '800px',
	},

	center: {
		position: 'absolute',
		top: token('space.0'),
		left: token('space.0'),
		height: '450px',
		width: '100%',
	},

	heroAnimated: {
		borderRadius: token('border.radius.300'),
		width: '700px',
		height: '394px',
		overflow: 'hidden',
	},

	bodyStack: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

const HeroImage = () => (
	<Box xcss={styles.heroImageContainer}>
		<Box xcss={styles.heroBackground}>
			<Image src={HeroBackground} alt="" />
		</Box>
		<Flex xcss={styles.center} alignItems="center" justifyContent="center">
			<Box xcss={styles.heroAnimated}>
				<Image src={CrossJoinHeroImage} alt="" />
			</Box>
		</Flex>
	</Box>
);

export interface ConfluenceAvailabilityTabBodyProps {
	confluenceUrl: string;
}

const ConfluenceAvailabilityTabBodyWithIntl = ({
	confluenceUrl,
	intl,
}: ConfluenceAvailabilityTabBodyProps & WrappedComponentProps) => {
	const { fireAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent({
			name: 'crossProductSearchConfluenceUpsellTab',
			eventType: 'screen',
		});
	}, [fireAnalyticsEvent]);

	const onLearnMoreClick = () =>
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'learnMoreButton',
			actionSubjectId: 'crossProductSearchConfluenceUpsellLearnMoreButton',
			eventType: 'ui',
		});

	const onJoinClick = () => {
		fireAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'joinButton',
			actionSubjectId: 'crossProductSearchConfluenceUpsellJoinButton',
			eventType: 'ui',
		});
	};

	return (
		<Stack testId="confluence-availability-tab-body" xcss={styles.bodyStack} space="space.200">
			<HeroImage />
			<Stack space="space.100">
				<Heading size="medium">
					{intl.formatMessage(messages.confluence_availability_header)}
				</Heading>
				<Text color="color.text.subtle">
					{intl.formatMessage(messages.confluence_availability_description)}
				</Text>
			</Stack>
			<Flex justifyContent="end" columnGap="space.100">
				<LinkButton
					appearance="subtle"
					href="https://www.atlassian.com/software/confluence"
					target="_blank"
					rel="noopener"
					testId="confluence-availability-learn-more-button"
					onClick={onLearnMoreClick}
				>
					{intl.formatMessage(messages.confluence_availability_learn_more_button)}
				</LinkButton>
				<LinkButton
					appearance="primary"
					href={confluenceUrl}
					target="_blank"
					rel="noopener"
					testId="confluence-availability-join-button"
					onClick={onJoinClick}
				>
					{intl.formatMessage(messages.confluence_availability_join_button)}
				</LinkButton>
			</Flex>
		</Stack>
	);
};

export const ConfluenceAvailabilityTabBody = injectIntl(ConfluenceAvailabilityTabBodyWithIntl);
